import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Label } from '../../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';

export interface MultiCompanyPickerProps {
  /** Selected company ids; empty array = shared across all companies. */
  value: string[];
  onChange: (companyIds: string[]) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  /**
   * If true, render even when the tenant only has one company. Default false:
   * hides itself in single-company tenants (canonical hide-when-single pattern).
   * See docs/platform/multi-company-design.md.
   */
  showWhenSingle?: boolean;
  className?: string;
  id?: string;
}

/**
 * Tag-style multi-select picker over the user's accessible companies. Used for
 * M-pattern columns (`company_ids[]`) on shared collaboration records:
 * organizations, contacts, products, price lists, tickets, KB articles,
 * workspaces, etc. Empty selection means "shared across all companies".
 *
 * Built on the shadcn `Command` component so the option list is fully keyboard
 * operable (↓/↑ highlight, Enter to toggle, type-to-filter) with the
 * listbox/option ARIA roles required by STYLE_GUIDE "Keyboard Navigation
 * (Selection Lists)". Auto-hides in single-company tenants (canonical
 * hide-when-single pattern).
 */
export function MultiCompanyPicker({
  value,
  onChange,
  label,
  helperText,
  disabled,
  showWhenSingle = false,
  className,
  id,
}: MultiCompanyPickerProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const { data, isLoading } = useMyCompanies();
  const companies = data?.data ?? [];
  const [open, setOpen] = useState(false);

  if (!showWhenSingle && companies.length <= 1) {
    return null;
  }

  const toggle = (companyId: string) => {
    if (value.includes(companyId)) {
      onChange(value.filter((cid) => cid !== companyId));
    } else {
      onChange([...value, companyId]);
    }
  };

  const remove = (companyId: string) => {
    onChange(value.filter((cid) => cid !== companyId));
  };

  const selectedCompanies = companies.filter((c) => value.includes(c.id));
  const triggerLabel =
    value.length === 0
      ? t('common:pickers.sharedAcrossCompanies', 'Shared (all companies)')
      : t('user_management:companies.tagSummary', '{{count}} tagged', { count: value.length });

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled || isLoading}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate text-sm">{triggerLabel}</span>
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ms-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('common:pickers.searchCompany')} />
            <CommandList>
              <CommandEmpty>{t('common:pickers.noCompanyFound')}</CommandEmpty>
              <CommandGroup>
                {companies.map((c) => {
                  const isSelected = value.includes(c.id);
                  return (
                    // Value carries the id so two companies sharing a name stay
                    // distinct items for cmdk's filter + keyboard highlight; the
                    // name prefix keeps type-to-filter matching on the name.
                    <CommandItem
                      key={c.id}
                      value={`${c.name} ${c.id}`}
                      onSelect={() => toggle(c.id)}
                    >
                      <Check
                        className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm flex-1 truncate ms-2">{c.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedCompanies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {selectedCompanies.map((c) => (
            <Badge key={c.id} variant="secondary" className="gap-1 pl-2 pr-1 py-0.5">
              <Building2 className="h-3 w-3" />
              <span className="text-xs">{c.name}</span>
              <button
                type="button"
                onClick={() => remove(c.id)}
                disabled={disabled}
                className="hover:bg-muted rounded-sm flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:p-0.5 -my-1 -me-1"
                aria-label={t('common:app.remove', 'Remove')}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}
